<template>
<div class="form-step-item">
    <div class="panel">
        <div class="intro">
            <h3>Hardware Requirements</h3>
            <div v-if="formData.patient_country === 'Australia' && formData.patient_type === 'nutrition'">
                <p>Allow up to 5 working days for delivery of hardware.</p>
                <p>All hardware on loan is subject to Baxter's Equipment on Loan T&amp;C's and any loss or damage my be charged for.</p>
                <p>If a pump is required, pump accessories can be sent with the pump delivery. Additional giving sets can also be ordered as consumables. For product details please log into <a href="#" target="_blank" rel="noopener noreferrer">Baxter Professional</a>. </p>
            </div>
            <div v-else>
                <p>Allow up to 5 working days for delivery of hardware. If a pump is ordered, 1 box of giving sets will be automatically ordered and delivered to the address provided. Additional giving sets are available for purchase.</p>
            </div>
        </div>
    </div>
    <div class="panel">
        <h4>Pump</h4>
        <div class="form-row">
            <div class="form-group col-4">
                <label for="pump_type">Type</label>
                <select v-model="formData.pump_type" class="form-control" @change="onPumpTypeChange(true)">
                    <option value="">- Not required -</option>
                    <option v-for="(pump, index) in defaultSettings.pumpsList" :value="pump.name" v-bind:key="index">
                        {{ pump.name }}
                    </option>
                </select>
            </div>
            <div class="form-group col-2" v-if="formData.pump_type !== ''">
                <label for="pump_qty">Quantity *</label>
                <input type="text" class="form-control" v-model="formData.pump_qty">
                <span class="form-error" v-if="$v.formData.pump_qty.$dirty && !$v.formData.pump_qty.required">Required field</span>
            </div>
            <div class="form-group col-3" v-if="formData.pump_type !== ''">
                <label for="pump_delivery_date">Delivery date *</label>
                <DateEntry class="form-control" v-model="formData.pump_delivery_date"></DateEntry>
                <span class="form-error" v-if="$v.formData.pump_delivery_date.$dirty && !$v.formData.pump_delivery_date.required">Required field</span>
                <span class="form-error" v-if="$v.formData.pump_delivery_date.$dirty && !$v.formData.pump_delivery_date.pastDate">Date not valid</span>
            </div>
            <div class="form-group col-3" v-if="formData.pump_type !== ''">
                <label for="pump_deliver_to">Deliver to *</label>
                <select v-model="formData.pump_deliver_to" class="form-control">
                    <option class="d-none" value="">- Select -</option>
                    <option v-for="(option, index) in deliveryOptions" :value="option" v-bind:key="index">
                        {{ option }}
                    </option>
                </select>
                <span class="form-error" v-if="$v.formData.pump_deliver_to.$dirty && !$v.formData.pump_deliver_to.required">Required field</span>
            </div>
        </div>
        <div v-if="formData.pump_type !== ''">
            <div class="form-row" v-if="formData.pump_deliver_to === 'Hospital' || formData.pump_deliver_to === 'Other address'">
                <div class="form-group col-12">
                    <label for="pump_delivery_address">Specific delivery address<span v-if="formData.pump_deliver_to === 'Hospital'"> for hospital</span> *</label>
                    <input type="text" class="form-control" v-model="formData.pump_delivery_address">
                    <span class="form-error" v-if="$v.formData.pump_delivery_address.$dirty && !$v.formData.pump_delivery_address.required">Required field</span>
                </div>
            </div>
            <div v-if="formData.patient_type === 'fluids' || formData.patient_country === 'New Zealand'">
                <h5 class="label">Is a box of giving sets required to be sent with the pump delivery? *</h5>
                <div class="form-row">
                    <div class="form-group col-4 giving-set-radio">
                        <div class="bax-boxed-radio">
                            <div class="bax-radio">
                                <input type="radio" id="pump_giving_set_required_yes" class="css-checkbox" value="yes" v-model="formData.pump_giving_set_required" />
                                <label for="pump_giving_set_required_yes" class="css-label">Yes</label>
                            </div>
                            <div class="bax-radio">
                                <input type="radio" id="pump_giving_set_required_no" class="css-checkbox" value="no" v-model="formData.pump_giving_set_required" />
                                <label for="pump_giving_set_required_no" class="css-label">No</label>
                            </div>
                        </div>
                        <span class="form-error" v-if="$v.formData.pump_giving_set_required.$dirty && !$v.formData.pump_giving_set_required.required">Required field</span>
                    </div>
                    <div class="form-group col-2 giving-set-yes-label" v-if="formData.pump_giving_set_required === 'yes' && givingSetOptions.length">
                        <label for="pump_giving_set_type">Please select type:</label>
                    </div>
                    <div class="form-group col-6" v-if="formData.pump_giving_set_required === 'yes' && givingSetOptions.length">
                        <select v-model="formData.pump_giving_set_type" class="form-control">
                            <option class="d-none" value="">- Select -</option>
                            <option v-for="(item, index) in givingSetOptions" :value="item" v-bind:key="index">
                                {{ item }}
                            </option>
                        </select>
                        <span class="form-error" v-if="$v.formData.pump_giving_set_type.$dirty && !$v.formData.pump_giving_set_type.required">Required field</span>
                    </div>
                </div>
            </div>
            <h5>Pump accessories to send with pump delivery</h5>
            <div class="pump-accessories-list">
            <p>Please select which pump accessories are required to be sent with the pump delivery.</p>
                <div class="pump-accessory-item" v-for="(item, index) in this.formData.pump_accessories_list" :key="index" >
                    <PumpAccessoryRow :index="index" :item="item" :pumpAccessoriesOptions="pumpAccessoriesOptions" :formData="formData" :ref="'pump_accessory_row_' + index" @pump_accessories_validation_response="OnPumpAccessoriesValidationResponse"></PumpAccessoryRow>
                </div>
                <button class="lists-add" v-on:click="addPumpAccessoryRow">Add another pump accessory to send with pump delivery</button>
            </div>
        </div>
    </div>
</div>
</template>


<script>

import { requiredIf } from 'vuelidate/lib/validators';
import moment from 'moment';
import DateEntry from '@/components/ui/DateEntry.vue';
import PumpAccessoryRow from '@/views/Tpn/PatientRegister/PumpAccessoryRow.vue';

export default {
    name: 'FormStep5',
    components: {
        DateEntry,
        PumpAccessoryRow,
    },
    props: {
        formData: {
            required: true,
        },
        defaultSettings: {
            required: true,
        },
    },
    async created() {
        this.onPumpTypeChange(false);
    },
    data() {
        return {
            validPumpAccessoriesList: true,
            givingSetOptions: [],
            pumpAccessoriesOptions: [],
            deliveryOptions: [
                'Hospital',
                'Home',
                'Other address',
            ],
        };
    },
    validations: {
        formData: {
            pump_qty: {
                required: requiredIf(function () {
                    if (this.formData.pump_type !== '') {
                        return true;
                    }
                    return false;
                }),
            },
            pump_delivery_date: {
                required: requiredIf(function () {
                    if (this.formData.pump_type !== '') {
                        return true;
                    }
                    return false;
                }),
                pastDate(val) {
                    if (val === '') {
                        return true;
                    }
                    if (moment(val, ['DD/MM/YYYY', 'D/M/YYYY'], true).isValid() === false) {
                        return true;
                    }
                    const today = moment().format('DD/MM/YYYY');
                    const now = moment(today, 'DD/MM/YYYY');
                    const date = moment(val, 'DD/MM/YYYY');

                    if (date.diff(now, 'days') <= 0) {
                        return false;
                    }

                    return true;
                },
            },
            pump_deliver_to: {
                required: requiredIf(function () {
                    if (this.formData.pump_type !== '') {
                        return true;
                    }
                    return false;
                }),
            },
            pump_delivery_address: {
                required: requiredIf(function () {
                    if (this.formData.pump_deliver_to === 'Hospital' || this.formData.pump_deliver_to === 'Other address') {
                        return true;
                    }
                    return false;
                }),
            },
            pump_giving_set_required: {
                required: requiredIf(function () {
                    if (this.formData.pump_type !== '' && (this.formData.patient_type === 'fluids' || this.formData.patient_country === 'New Zealand')) {
                        return true;
                    }
                    return false;
                }),
            },
            pump_giving_set_type: {
                required: requiredIf(function () {
                    if (this.formData.pump_type !== '' && (this.formData.patient_type === 'fluids' || this.formData.patient_country === 'New Zealand') && this.formData.pump_giving_set_required === 'yes' && this.givingSetOptions.length) {
                        return true;
                    }
                    return false;
                }),
            },
        },
    },
    computed: {
    },
    methods: {
        validateForm() {
            this.$v.$touch();

            // loop through pump accessories to run validation
            if (this.formData.pump_type !== '' && this.formData.pump_accessories_list.length) {
                for (let i = 0; i < this.formData.pump_accessories_list.length; i += 1) {
                    this.$refs['pump_accessory_row_' + i][0].validateForm();
                }
            }
            this.$emit('validation_response', !this.$v.$invalid && this.validPumpAccessoriesList);
        },
        OnPumpAccessoriesValidationResponse(response) {
            this.validPumpAccessoriesList = response;
        },
        addPumpAccessoryRow() {
            this.formData.pump_accessories_list.push({ name: '' });
        },
        onPumpTypeChange(reset) {
            // reset giving set and accessories options for selects
            this.givingSetOptions = [];
            this.pumpAccessoriesOptions = [];
            this.validPumpAccessoriesList = true;

            // reset repeater form accessories list
            if (reset) {
                this.formData.pump_accessories_list = [{ name: '' }];
            }

            // search selected pump item in data
            const item = this.defaultSettings.pumpsList.find((pumpType) => pumpType.name === this.formData.pump_type);

            if (item) {
                // set giving sets options for select
                if (item.givingSets.length > 1) {
                    item.givingSets.forEach((el) => {
                        this.givingSetOptions.push(el);
                    });
                }

                // add giving sets to accessories options for select when not showing giving set selection
                if (this.formData.patient_type === 'nutrition' && this.formData.patient_country === 'Australia') {
                    this.pumpAccessoriesOptions.push({ category: 'Giving sets', items: item.givingSets });
                }

                // set accessories options for select
                item.accessories.forEach((el) => {
                    this.pumpAccessoriesOptions.push(el);
                });
            }
        },
    },
};
</script>
